import { useState } from "react";
import { toast } from "sonner";
import { BeatShell } from "@/components/nutrition/NutritionParts";
import { shuffle } from "../beatUtils";
import { Briefing } from "./Shared";
import { ListOrdered, GripVertical, X, Check, RotateCcw } from "lucide-react";

export function OrderBeat({ beat, eyebrow, showBriefing, c, accent, onDone }) {
  const correctOrder = beat.items.map((i) => i.id);
  const [tray, setTray] = useState(() => shuffle(beat.items));
  const [seq, setSeq] = useState([]);
  const [res, setRes] = useState(null);
  const checked = !!res;
  const place = (item) => { if (checked) return; setTray((t) => t.filter((x) => x.id !== item.id)); setSeq((s) => [...s, item]); };
  const remove = (item) => { if (checked) return; setSeq((s) => s.filter((x) => x.id !== item.id)); setTray((t) => [...t, item]); };
  const reset = () => { if (checked) return; setSeq([]); setTray(shuffle(beat.items)); };
  const onPrimary = () => {
    if (!checked) {
      if (seq.length < beat.items.length) return toast.error("Place every step in order.");
      const correctCount = seq.filter((it, i) => it.id === correctOrder[i]).length;
      const full = beat.full ?? 25;
      setRes({ earned: Math.round((full * correctCount) / beat.items.length), max: full, correctCount,
        detail: `${correctCount}/${beat.items.length} steps in the right place`, key: beat.key, label: beat.label });
    } else onDone(res);
  };
  return (
    <BeatShell Icon={beat.icon || ListOrdered} eyebrow={eyebrow} title={beat.title} subtitle={beat.prompt}
      primaryLabel={checked ? beat.last ? "See debrief →" : "Continue →" : "Check order"} onPrimary={onPrimary} primaryTestId={`${beat.key}-primary-btn`} accent={accent}>
      {showBriefing && <Briefing c={c} />}
      <div className="space-y-2" data-testid={`${beat.key}-sequence`}>
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Put the steps in order</span>
          {seq.length > 0 && !checked && (
            <button
              type="button"
              onClick={reset}
              data-testid={`${beat.key}-reset-btn`}
              className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-[#0047AB] transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset &amp; start again
            </button>
          )}
        </div>
        {Array.from({ length: beat.items.length }).map((_, i) => {
          const it = seq[i];
          const ok = checked && it && it.id === correctOrder[i];
          const bad = checked && it && it.id !== correctOrder[i];
          return (
            <div key={i} data-testid={`${beat.key}-slot-${i}`}
              className={`flex items-center gap-3 rounded-md border-2 px-3 py-2.5 min-h-[48px] transition-colors ${ok ? "border-emerald-300 bg-emerald-50" : bad ? "border-rose-300 bg-rose-50" : it ? "border-slate-300 bg-white" : "border-dashed border-slate-200 bg-slate-50"}`}>
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0" style={{ background: it ? accent : "#cbd5e1" }}>{i + 1}</span>
              {it ? (
                <button type="button" disabled={checked} onClick={() => remove(it)} data-testid={`${beat.key}-placed-${it.id}`} className="flex-1 text-left text-sm text-slate-800">{it.text}</button>
              ) : <span className="text-sm text-slate-400">Tap a step below…</span>}
              {checked && it && (ok ? <Check className="w-4 h-4 text-emerald-600 shrink-0" /> : <X className="w-4 h-4 text-rose-500 shrink-0" />)}
            </div>
          );
        })}
      </div>
      {tray.length > 0 && !checked && (
        <div className="flex flex-wrap gap-2 pt-1" data-testid={`${beat.key}-tray`}>
          {tray.map((it) => (
            <button key={it.id} type="button" onClick={() => place(it)} data-testid={`${beat.key}-tray-${it.id}`}
              className="flex items-center gap-1.5 rounded-md border-2 border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:border-slate-400 transition-colors">
              <GripVertical className="w-3.5 h-3.5 text-slate-400" />{it.text}
            </button>
          ))}
        </div>
      )}
      {checked && (
        <div className="rounded-md bg-slate-50 border border-slate-200 p-2.5 mt-1" data-testid={`${beat.key}-order-key`}>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Correct order</div>
          <ol className="text-[11px] text-slate-600 list-decimal list-inside space-y-0.5">
            {beat.items.map((it) => <li key={it.id}>{it.text}</li>)}
          </ol>
        </div>
      )}
    </BeatShell>
  );
}
